import {useEffect, useState} from "react";
import {useAuthInfo} from "@propelauth/react";
import {PlugKind, PlugsView} from "../bid/bidding_model";
import {levelingRequest} from "./plugs";
import {useToast} from "../ui/use-toast";

// server: backend/feedback/feedback_models.py (models), backend/leveling/ai_leveling.py (logic)

// v3 review digest - smart_feedback_v3 BidReview.scope_items
export type ScopeItemStatus = "included" | "excluded" | "qualified" | "allowance" | "not_mentioned";
export interface ScopeItem { item: string; status: ScopeItemStatus; amount: number | null; note: string }

// what claude returns
export interface Coverage { bidder: string; status: ScopeItemStatus; note: string }
export interface ItemRow { item: string; coverage: Coverage[] }
export interface PlugIdea { line: string | null; kind: PlugKind; amount: number; note: string; reason: string }
export interface BidLeveling {
    summary: string;
    items: ItemRow[];
    approach: string[];
    clarify: string[];
    plugs: PlugIdea[];
}

// what is stored
export interface LevelingBidder {
    key: string;                   // "B0" = this bid, then "B1".. by leveled amount
    bid_id: string;
    company_id: string | null;
    company_name: string;
    submitted_at: string | null;
    comparable: boolean;           // false - SOV on an older template, its lines were not sent
    reviewed: boolean;             // false - no AI review, sent with its form only
}
export type SuggestionState = "pending" | "accepted" | "rejected";
export interface SuggestedPlug {
    id: string;
    cell_id: number | null;        // the SOV cell id. null = base bid
    amount: number;
    note: string;
    kind: PlugKind;
    label: string;                 // SOV line label when suggested
    reason: string;
    others: Record<string, number | string | null>;   // bidder key -> value on this line
    state: SuggestionState;
    plug_id: string | null;
    decided_by: string | null;
    decided_at: string | null;
}
export interface AILevelingView {
    id: string;
    project_id: string;
    scope_id: string | null;
    bid_id: string;
    company_id: string | null;
    status: "running" | "done" | "failed";
    error: string | null;
    started_at: string;
    finished_at: string | null;
    started_by: string | null;
    file_ids: string[];
    skipped_files: string[];
    bids: LevelingBidder[];        // B0 first
    result: BidLeveling | null;
    plugs: SuggestedPlug[];
    stale: boolean;                // the other bids changed since the run
}

export const SCOPE_STATUS: Record<ScopeItemStatus, { label: string, style: string }> = {
    included: {label: "incl", style: "bg-green-100 text-green-800"},
    excluded: {label: "excl", style: "bg-red-100 text-red-800"},
    qualified: {label: "qual", style: "bg-amber-100 text-amber-800"},
    allowance: {label: "allow", style: "bg-blue-100 text-blue-800"},
    not_mentioned: {label: "—", style: "bg-gray-100 text-gray-500"},
};

// a pending suggestion - dashed, so it never reads as a plug that is already counted
export const SUGGESTION_LINE = "border border-dashed border-sky-300 bg-sky-50/60";
export const SUGGESTION_TAG = "bg-sky-100 text-sky-800";

const levelingUrl = (project_id: string, bid_id: string) => `/projects/${project_id}/bidding/${bid_id}/leveling_ai`;

export function getLeveling(project_id: string, bid_id: string, token: string): Promise<AILevelingView | null> {
    return levelingRequest("GET", levelingUrl(project_id, bid_id), token);
}

export function generateLeveling(project_id: string, bid_id: string, token: string): Promise<AILevelingView> {
    return levelingRequest("POST", `${levelingUrl(project_id, bid_id)}/generate`, token, {});
}

// the suggestion becomes a plug (source ai). edits change it first
export function acceptSuggestion(project_id: string, bid_id: string, sid: string,
                                 edits: { amount?: number, note?: string, kind?: PlugKind },
                                 token: string): Promise<PlugsView> {
    return levelingRequest("POST", `${levelingUrl(project_id, bid_id)}/plugs/${sid}/accept`, token, edits);
}

export function rejectSuggestion(project_id: string, bid_id: string, sid: string,
                                 token: string): Promise<AILevelingView> {
    return levelingRequest("POST", `${levelingUrl(project_id, bid_id)}/plugs/${sid}/reject`, token, {});
}

export interface BidLevelingState {
    doc: AILevelingView | null;
    loaded: boolean;
    pending: SuggestedPlug[];
    // reviews the bid first if it has no review, then levels it - resolves once both are claimed
    generate: () => Promise<void>;
    accept: (s: SuggestedPlug, edits: { amount?: number, note?: string, kind?: PlugKind }) => Promise<PlugsView>;
    reject: (s: SuggestedPlug) => void;
}

/**
 * The bid's AI leveling - one per BidPage, shared by the AI review row and the SOV's suggestion lines.
 * Polls while it runs, like SmartFeedbackRow. enabled false (builders, drafts) - never calls the server.
 * accept resolves to the bid's plugs - pass it to the page's onPlugsChanged.
 * */
export function useBidLeveling(project_id: string, bid_id: string, enabled: boolean): BidLevelingState {
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const [doc, setDoc] = useState<AILevelingView | null>(null);
    const [loaded, setLoaded] = useState<boolean>(false);
    const token = accessToken as string;

    const onError = (error: any) => {
        console.error("ai leveling:", error);
        toast({title: "Error", description: error?.message || "Something went wrong with AI leveling.",
            variant: "destructive"});
    }

    const refresh = () => getLeveling(project_id, bid_id, token).then(setDoc).catch(onError);

    useEffect(() => {
        setDoc(null);
        setLoaded(false);
        if (!enabled) return;
        getLeveling(project_id, bid_id, token)
            .then(setDoc)
            .catch(onError)
            .finally(() => setLoaded(true));
    }, [project_id, bid_id, enabled]);

    // poll while it runs in the background
    useEffect(() => {
        if (doc?.status !== "running") return;
        const timer = setTimeout(refresh, 5000);
        return () => clearTimeout(timer);
    }, [doc]);

    const generate = () => generateLeveling(project_id, bid_id, token).then(setDoc).catch(onError);

    const accept = (s: SuggestedPlug, edits: { amount?: number, note?: string, kind?: PlugKind }) =>
        acceptSuggestion(project_id, bid_id, s.id, edits, token)
            .then((view) => {
                refresh();
                return view;
            })
            .catch((error) => {
                onError(error);
                throw error;
            });

    const reject = (s: SuggestedPlug) => {
        rejectSuggestion(project_id, bid_id, s.id, token).then(setDoc).catch(onError);
    }

    const pending = doc?.status === "done" ? doc.plugs.filter((p) => p.state === "pending") : [];
    return {doc, loaded, pending, generate, accept, reject};
}
